import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';

interface FormSubmission {
  _id: string;
  submissionId: string;
  annonsor: string;
  stillingstittel: string;
  arbeidsgiver: string;
  kampanjenavn: string;
  ordrenumre: string[];
  submittedAt: string;
  emailSent: boolean;
  asanaTaskCreated: boolean;
  asanaTaskId?: string;
  pdfGenerated: boolean;
  formData: any;
  userId?: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    role: string;
    department?: string;
    newspaper?: string;
  };
  userSnapshot: {
    firstName: string;
    lastName: string;
    email: string;
    role: string;
    department?: string;
    newspaper?: string;
  };
}

const SubmissionDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [submission, setSubmission] = useState<FormSubmission | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Determine if this is an admin view or user view
  const isAdminView = window.location.pathname.includes('/dashboard/submissions');
  const isUserView = window.location.pathname.includes('/my-orders');

  useEffect(() => {
    if (!id) return;

    const fetchSubmission = async () => {
      try {
        setLoading(true);
        const endpoint = isAdminView 
          ? `${process.env.REACT_APP_API_URL}/forms/submissions/all/${id}`
          : `${process.env.REACT_APP_API_URL}/forms/submissions/my/${id}`;

        const response = await fetch(endpoint, {
          credentials: 'include'
        });

        if (response.ok) {
          const data: FormSubmission = await response.json();
          setSubmission(data);
        } else if (response.status === 404) {
          setError('Innsendingen ble ikke funnet');
        } else if (response.status === 403) {
          setError('Du har ikke tilgang til denne innsendingen');
        } else {
          setError('Feil ved henting av innsending');
        }
      } catch (error) {
        console.error('Error fetching submission:', error);
        setError('Feil ved henting av innsending');
      } finally {
        setLoading(false);
      }
    };

    fetchSubmission();
  }, [id, isAdminView]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('no-NO');
  };

  const formatArrayValue = (value: any) => {
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    return value || 'Ikke utfylt';
  };

  const goBack = () => {
    if (isAdminView) {
      navigate('/dashboard/submissions');
    } else {
      navigate('/my-orders');
    }
  };

  if (loading) {
    return (
      <div className="dashboard-page">
        <Header />
        <div className="dashboard-content">
          <div className="container">
            <div className="loading">Laster innsending...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !submission) {
    return (
      <div className="dashboard-page">
        <Header />
        <div className="dashboard-content">
          <div className="container">
            <div className="error-message">
              <h2>Feil</h2>
              <p>{error || 'Innsendingen kunne ikke lastes'}</p>
              <button className="btn btn-secondary" onClick={goBack}>
                Gå tilbake
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { formData } = submission;

  return (
    <div className="dashboard-page">
      <Header />
      <div className="dashboard-content">
        <div className="container">
          <div className="page-header">
            <h1>Detaljer for innsending</h1>
            <button className="btn btn-secondary" onClick={goBack}>
              ← Tilbake
            </button>
          </div>

          {/* Status Overview */}
          <div className="status-overview">
            <div className="status-card">
              <h3>Status oversikt</h3>
              <div className="status-grid">
                <div className={`status-item ${submission.emailSent ? 'success' : 'error'}`}>
                  <span className="status-icon">{submission.emailSent ? '✓' : '✗'}</span>
                  <span>E-post sendt</span>
                </div>
                <div className={`status-item ${submission.asanaTaskCreated ? 'success' : 'error'}`}>
                  <span className="status-icon">{submission.asanaTaskCreated ? '✓' : '✗'}</span>
                  <span>Asana oppgave opprettet</span>
                  {submission.asanaTaskId && (
                    <small>ID: {submission.asanaTaskId}</small>
                  )}
                </div>
                <div className={`status-item ${submission.pdfGenerated ? 'success' : 'error'}`}>
                  <span className="status-icon">{submission.pdfGenerated ? '✓' : '✗'}</span>
                  <span>PDF generert</span>
                </div>
              </div>
            </div>

            <div className="meta-info">
              <h3>Metadata</h3>
              <div className="meta-grid">
                <div><strong>Innsending ID:</strong> {submission.submissionId}</div>
                <div><strong>Sendt inn:</strong> {formatDate(submission.submittedAt)}</div>
                <div><strong>Kampanjenavn:</strong> {submission.kampanjenavn}</div>
                {isAdminView && (
                  <>
                    <div><strong>Innsender:</strong> {submission.userSnapshot.firstName} {submission.userSnapshot.lastName}</div>
                    <div><strong>Avdeling:</strong> {submission.userSnapshot.department || 'N/A'}</div>
                    <div><strong>Avis:</strong> {submission.userSnapshot.newspaper || 'N/A'}</div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Form Data Display */}
          <div className="form-data-display">
            <h2>Skjemadata</h2>
            
            {/* Selger Information */}
            <div className="data-section">
              <h3>Selger</h3>
              <div className="data-grid">
                <div><strong>Fornavn:</strong> {formData.selger?.fornavn || 'Ikke utfylt'}</div>
                <div><strong>Etternavn:</strong> {formData.selger?.etternavn || 'Ikke utfylt'}</div>
                <div><strong>E-post:</strong> {formData.selger?.epost || 'Ikke utfylt'}</div>
                <div><strong>Rolle:</strong> {formData.selger?.rolle || 'Ikke utfylt'}</div>
              </div>
            </div>

            {/* Kampanjeinfo */}
            <div className="data-section">
              <h3>Kampanjeinfo</h3>
              <div className="data-grid">
                <div><strong>Annonsør:</strong> {formData.annonsor}</div>
                <div><strong>Handlekurvnr:</strong> {formData.handlekurvnr}</div>
                <div><strong>Ordrenr:</strong> {Array.isArray(formData.ordrenumre) ? formData.ordrenumre.join(', ') : (formData.ordrenrCrossAd || 'N/A')}</div>
                <div><strong>Stillingstittel:</strong> {formData.stillingstittel}</div>
                <div><strong>Arbeidsgiver:</strong> {formData.arbeidsgiver}</div>
                <div><strong>Kampanjenavn:</strong> {formData.kampanjenavn}</div>
                <div><strong>Link til landingsside:</strong> {formData.landingsside || 'Ikke utfylt'}</div>
                <div><strong>Stillingskarusell:</strong> {formData.stillingskarusell}</div>
              </div>
            </div>

            {/* Mål og målstyring */}
            <div className="data-section">
              <h3>Mål og målstyring</h3>
              <div className="data-grid">
                <div><strong>Målsetning:</strong> {formatArrayValue(formData.malsetning)}</div>
              </div>

              {/* Facebook/Instagram */}
              {formData.facebookInstagramEnabled && (
                <div className="campaign-section">
                  <h4>Facebook / Instagram</h4>
                  <div className="data-grid">
                    <div><strong>Spending totalt:</strong> {formData.spendingTotalt || 'Ikke utfylt'} kr</div>
                    <div><strong>Hvorav retargeting:</strong> {formData.hvoravRetargeting || 'Ikke utfylt'} kr</div>
                    <div><strong>Annonseformat:</strong> {formatArrayValue(formData.annonseformat)}</div>
                    <div><strong>Geotargeting:</strong> {formData.geotargeting || 'Ikke utfylt'}</div>
                    <div><strong>Periode:</strong> {formData.fra && formData.til ? `${formData.fra} - ${formData.til}` : 'Ikke utfylt'}</div>
                  </div>
                </div>
              )}

              {/* LinkedIn */}
              {formData.linkedinEnabled && (
                <div className="campaign-section">
                  <h4>LinkedIn</h4>
                  <div className="data-grid">
                    <div><strong>Spending totalt:</strong> {formData.linkedinSpendingTotalt || 'Ikke utfylt'} kr</div>
                    <div><strong>Hvorav retargeting:</strong> {formData.linkedinHvoravRetargeting || 'Ikke utfylt'} kr</div>
                    <div><strong>Annonseformat:</strong> {formatArrayValue(formData.linkedinAnnonseformat)}</div>
                    <div><strong>Geotargeting:</strong> {formData.linkedinGeotargeting || 'Ikke utfylt'}</div>
                    <div><strong>Målstyring:</strong> {formData.linkedinMalstyring || 'Ikke utfylt'}</div>
                    <div><strong>Periode:</strong> {formData.linkedinFra && formData.linkedinTil ? `${formData.linkedinFra} - ${formData.linkedinTil}` : 'Ikke utfylt'}</div>
                  </div>
                </div>
              )}

              {/* Andre kanaler */}
              {formData.andreKanalerEnabled && formData.andreKanaler && formData.andreKanaler.length > 0 && (
                <div className="campaign-section">
                  <h4>Andre kanaler</h4>
                  {formData.andreKanaler.map((kanal: any, index: number) => (
                    <div key={index} className="sub-campaign">
                      <h5>Kanal {index + 1}: {kanal.kanal || 'Ukjent'}</h5>
                      <div className="data-grid">
                        <div><strong>Spending totalt:</strong> {kanal.spendingTotalt || 'Ikke utfylt'} kr</div>
                        <div><strong>Hvorav retargeting:</strong> {kanal.hvoravRetargeting || 'Ikke utfylt'} kr</div>
                        <div><strong>Annonseformat:</strong> {formatArrayValue(kanal.annonseformat)}</div>
                        <div><strong>Geotargeting:</strong> {kanal.geotargeting || 'Ikke utfylt'}</div>
                        <div><strong>Målstyring:</strong> {kanal.malstyring || 'Ikke utfylt'}</div>
                        <div><strong>Periode:</strong> {kanal.fra && kanal.til ? `${kanal.fra} - ${kanal.til}` : 'Ikke utfylt'}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Innlegg/feed-tekst */}
            <div className="data-section">
              <h3>Innlegg/feed-tekst</h3>
              <div className="data-grid">
                <div className="full-width"><strong>Kort tekst om stillingen:</strong> {formData.kortTekstStilling || 'Ikke utfylt'}</div>
                <div><strong>Søknadsfrist:</strong> {formData.soknadsfrist || 'Ikke utfylt'}</div>
                <div className="full-width"><strong>Hvilken kanal skal det postes fra:</strong> {formData.hvilkenKanalPosting || 'Ikke utfylt'}</div>
                <div className="full-width"><strong>Øvrig info for oppdraget:</strong> {formData.ovrigInfoOppdrag || 'Ikke utfylt'}</div>
              </div>
            </div>

            {/* Grafisk Produksjon */}
            <div className="data-section">
              <h3>Grafisk Produksjon</h3>
              <div className="data-grid">
                <div><strong>Grafisk produksjon:</strong> {formatArrayValue(formData.grafiskProduksjon)}</div>
                {(formData.rosenumre || formData.rosenummer) && <div><strong>Rosenummer:</strong> {formData.rosenumre ? formatArrayValue(formData.rosenumre) : formData.rosenummer}</div>}
                {formData.displayTyper && <div><strong>Display typer:</strong> {formatArrayValue(formData.displayTyper)}</div>}
                {formData.displayformat && <div><strong>Displayformat:</strong> {formatArrayValue(formData.displayformat)}</div>}
                {formData.stillingskarusellFra && (
                  <div><strong>Stillingskarusell periode:</strong> {formData.stillingskarusellFra} - {formData.stillingskarusellTil || 'Ikke utfylt'}</div>
                )}
                {formData.skalPaMn24 && <div><strong>Skal på Mn24.no:</strong> Ja</div>}
                {formData.hvissAnnet && <div className="full-width"><strong>Hvis annet:</strong> {formData.hvissAnnet}</div>}
              </div>
            </div>

            {/* Korrektur Mail */}
            <div className="data-section">
              <h3>Korrektur Mail</h3>
              <div className="data-grid">
                <div><strong>Korrektur dato:</strong> {formData.korrekturDato || 'Ikke utfylt'}</div>
                <div><strong>Korrektur E-post:</strong> {formData.korrekturEpost || 'Ikke utfylt'}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubmissionDetails;